import React, { createContext, useState, useContext, ReactNode } from "react";
import Scope from "../apiService/scope/scopeApiService";

// ----------- Type Definitions -----------
interface ScopeContextType {
  loading: boolean;
  error: string;
  scopes: any[];
  selectedScope: any | null;

  addScope: (scope: any) => Promise<void>;
  deleteScope: (scopeId: string) => Promise<void>;
  editScope: (scopeId: string, data: any) => Promise<void>;
  getScope: (scopeId: string) => Promise<void>;
  getAllScopes: () => Promise<void>;
}

// ----------- Context Create -----------
export const ScopeContext = createContext<ScopeContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const ScopeProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [scopes, setScopes] = useState<any[]>([]);
  const [selectedScope, setSelectedScope] = useState<any | null>(null);

  // ----------- API Calls Using Scope Service -----------
  const addScope = async (scope: any) => {
    setLoading(true);
    setError("");
    try {
      await Scope.addScope(scope);
      await getAllScopes(); // Refresh list after adding
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add scope");
    } finally {
      setLoading(false);
    }
  };

  const deleteScope = async (scopeId: string) => {
    setLoading(true);
    setError("");
    try {
      await Scope.deleteScope(scopeId);
      await getAllScopes(); // Refresh list after deletion
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete scope");
    } finally {
      setLoading(false);
    }
  };

  const editScope = async (scopeId: string, data: any) => {
    setLoading(true);
    setError("");
    try {
      await Scope.editScope(scopeId, data);
      await getAllScopes(); // Refresh list after editing
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to edit scope");
    } finally {
      setLoading(false);
    }
  };

  const getScope = async (scopeId: string) => {
    setLoading(true);
    setError("");
    try {
      const response = await Scope.getScope(scopeId);
      setSelectedScope(response.body.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch scope");
    } finally {
      setLoading(false);
    }
  };

  const getAllScopes = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await Scope.getAllScopes();
      setScopes(response.body.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch scopes");
    } finally {
      setLoading(false);
    }
  };

  const value = {
    loading,
    error,
    scopes,
    selectedScope,
    addScope,
    deleteScope,
    editScope,
    getScope,
    getAllScopes,
  };

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

// ----------- Custom Hook -----------
export const useScope = (): ScopeContextType => {
  const context = useContext(ScopeContext);
  if (context === undefined) {
    throw new Error("useScope must be used within a ScopeProvider");
  }
  return context;
}; 